import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import apiClient from '../../services/apiClient';
import {
  exportToCSV,
  exportToJSON,
  exportToExcel,
  exportToPDF,
  formatForExport,
  getExportFilename
} from '../../utils/exportUtils';
import {
  getDripCampaigns,
  getDripCampaign,
  createDripCampaign,
  updateDripCampaign,
  deleteDripCampaign,
  toggleDripCampaignStatus,
  duplicateDripCampaign,
  getDripCampaignStats,
  getDripCampaignAnalytics,
  getDripCampaignEnrollments,
  enrollMemberInCampaign,
  unenrollMemberFromCampaign,
  exportDripCampaign,
  sendBroadcastToCampaign,
  triggerCampaignFlow,
  clearApiMessage,
  clearError,
  clearDripCampaignState,
  selectDripCampaigns,
  selectDripCampaignPagination,
  selectCurrentDripCampaign,
  selectDripCampaignAnalytics,
  selectDripCampaignEnrollments,
  selectDripCampaignStats,
  selectDripCampaignsLoading,
  selectDripCampaignsSubmitting,
  selectDripCampaignsApiMessage,
  selectDripCampaignsError,
} from '../../store/slices/growth/dripCampaignSlice';
import {
  RefreshCw, Trash2, FileEdit, ClipboardList, AlertTriangle, Loader2, Zap,
  Pencil, BarChart3, Send, TrendingUp, Target, Download,
  MessageSquare, Users, Search, FileText, Save, Copy
} from 'lucide-react';

const DripCampaigns = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [broadcastMessage, setBroadcastMessage] = useState('');

  // ==================== CREATE STEPS STATE ====================
  const [createSteps, setCreateSteps] = useState([
    { id: Date.now() + 0, day: 'Day 0', msg: '' }
  ]);

  // ==================== EDIT STEPS STATE ====================
  const [editSteps, setEditSteps] = useState([]);

  // ==================== REFS ====================
  const editNameRef = useRef(null);
  const editTriggerRef = useRef(null);
  const createNameRef = useRef(null);
  const createTriggerRef = useRef(null);

  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const campaigns = useSelector(selectDripCampaigns) || [];
  const pagination = useSelector(selectDripCampaignPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentCampaign = useSelector(selectCurrentDripCampaign);
  const analytics = useSelector(selectDripCampaignAnalytics);
  const enrollments = useSelector(selectDripCampaignEnrollments) || [];
  const stats = useSelector(selectDripCampaignStats);
  const loading = useSelector(selectDripCampaignsLoading);
  const isSubmittingRedux = useSelector(selectDripCampaignsSubmitting);
  const apiMessage = useSelector(selectDripCampaignsApiMessage);
  const error = useSelector(selectDripCampaignsError);

  // ==================== EFFECTS ====================

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getDripCampaigns({
          per_page: itemsPerPage,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          trigger: triggerFilter
        }));
        await dispatch(getDripCampaignStats());
      } catch (err) {
        console.error('Error loading campaigns:', err);
      }
    };

    loadData();

    return () => {
      dispatch(clearDripCampaignState());
    };
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (statusFilter) params.status = statusFilter;
      if (triggerFilter) params.trigger = triggerFilter;

      dispatch(getDripCampaigns(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, triggerFilter, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  // Display API messages - with deduplication (same as Members component)
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;

    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== CREATE STEP FUNCTIONS ====================

  const addCreateStep = () => {
    const newId = Date.now() + Math.random();
    const nextDay = createSteps.length * 7;
    setCreateSteps(prev => [
      ...prev,
      { id: newId, day: `Day ${nextDay}`, msg: '' }
    ]);
  };

  const removeCreateStep = (id) => {
    if (createSteps.length <= 1) {
      showToastMessage('Cannot remove the last step', 'error');
      return;
    }
    setCreateSteps(prev => prev.filter(s => s.id !== id));
  };

  const updateCreateStep = (id, field, value) => {
    setCreateSteps(prev => prev.map(s =>
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const resetCreateSteps = () => {
    setCreateSteps([
      { id: Date.now() + 0, day: 'Day 0', msg: '' }
    ]);
  };

  // ==================== EDIT STEP FUNCTIONS ====================

  const addEditStep = () => {
    const newId = Date.now() + Math.random();
    const nextDay = editSteps.length * 7;
    setEditSteps(prev => [
      ...prev,
      { id: newId, day: `Day ${nextDay}`, msg: '' }
    ]);
  };

  const removeEditStep = (id) => {
    if (editSteps.length <= 1) {
      showToastMessage('Cannot remove the last step', 'error');
      return;
    }
    setEditSteps(prev => prev.filter(s => s.id !== id));
  };

  const updateEditStep = (id, field, value) => {
    setEditSteps(prev => prev.map(s =>
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  // ==================== HELPER FUNCTIONS ====================

  const getUniqueTriggers = () => {
    return [...new Set(campaigns.map(c => c?.trigger || ''))];
  };

  // ==================== PAGINATION ====================

  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== CAMPAIGN CRUD ====================

  const toggleCampaign = async (id, active) => {
    try {
      const result = await dispatch(toggleDripCampaignStatus(id)).unwrap();
      if (result.success) {
        await dispatch(getDripCampaigns({
          per_page: itemsPerPage,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          trigger: triggerFilter
        }));
        await dispatch(getDripCampaignStats());
        const campaignName = campaigns.find(c => c.id === id)?.name || 'Campaign';
        showToastMessage(`🔄 "${campaignName}" ${active ? 'activated' : 'paused'}`, active ? 'success' : 'info');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle campaign status', 'error');
    }
  };

  // ==================== CREATE CAMPAIGN ====================

  const openCreateDrip = () => {
    resetCreateSteps();

    setTimeout(() => {
      if (createNameRef.current) createNameRef.current.value = '';
      if (createTriggerRef.current) createTriggerRef.current.value = '';
    }, 0);

    const CreateModalContent = () => {
      const [localSteps, setLocalSteps] = useState(createSteps);

      useEffect(() => {
        setLocalSteps(createSteps);
      }, [createSteps]);

      const handleAddStep = () => {
        const newId = Date.now() + Math.random();
        const nextDay = localSteps.length * 7;
        const newSteps = [...localSteps, { id: newId, day: `Day ${nextDay}`, msg: '' }];
        setLocalSteps(newSteps);
        setCreateSteps(newSteps);
      };

      const handleRemoveStep = (id) => {
        if (localSteps.length <= 1) {
          showToastMessage('Cannot remove the last step', 'error');
          return;
        }
        const newSteps = localSteps.filter(s => s.id !== id);
        setLocalSteps(newSteps);
        setCreateSteps(newSteps);
      };

      const handleStepChange = (id, field, value) => {
        const newSteps = localSteps.map(s =>
          s.id === id ? { ...s, [field]: value } : s
        );
        setLocalSteps(newSteps);
        setCreateSteps(newSteps);
      };

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><RefreshCw size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />New Drip Campaign</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Campaign Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={createNameRef} placeholder="New Visitor Welcome Sequence" />
            </div>
            <div className="form-group">
              <label className="form-label">Trigger <span style={{ color: 'var(--red)' }}>*</span></label>
              <select className="form-select" ref={createTriggerRef} defaultValue="">
                <option value="">Select trigger...</option>
                <option value="Member status = Visitor">Member status = Visitor (new visitor)</option>
                <option value="Member status changes to Active">Member status changes to Active</option>
                <option value="Member opts in with SMS keyword">Member opts in with SMS keyword</option>
                <option value="Member absent for 30+ days">Member absent for 30+ days</option>
                <option value="Manual enrollment by admin">Manual enrollment by admin</option>
              </select>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>Campaign Steps</div>
                <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>{localSteps.length} steps</span>
              </div>
              {localSteps.map((s, index) => {
                const dayValue = s?.day || `Day ${index * 7}`;
                return (
                  <div key={s.id} style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' }}>
                    <div style={{
                      minWidth: '52px',
                      padding: '4px 8px',
                      background: 'var(--navy)',
                      color: 'var(--gold-light)',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textAlign: 'center',
                      flexShrink: 0,
                      marginTop: '4px'
                    }}>
                      <input
                        type="text"
                        value={dayValue}
                        onChange={(e) => handleStepChange(s.id, 'day', e.target.value)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--gold-light)',
                          fontSize: '11px',
                          fontWeight: '700',
                          textAlign: 'center',
                          width: '100%',
                          outline: 'none'
                        }}
                        placeholder="Day X"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <textarea
                        className="form-textarea"
                        rows="2"
                        style={{ minHeight: '50px' }}
                        value={s?.msg || ''}
                        onChange={(e) => handleStepChange(s.id, 'msg', e.target.value)}
                        placeholder={`Enter message for ${dayValue}...`}
                      />
                    </div>
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      onClick={() => handleRemoveStep(s.id)}
                      style={{ color: 'var(--red)', marginTop: '4px' }}
                      title="Remove step"
                      disabled={localSteps.length <= 1}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleAddStep}
                style={{ marginTop: '4px' }}
              >
                + Add Step
              </button>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              disabled={isSubmittingRedux}
              onClick={async () => {
                const name = createNameRef.current?.value?.trim() || '';
                const trigger = createTriggerRef.current?.value || '';

                if (!name) {
                  showToastMessage('Please enter a campaign name', 'error');
                  return;
                }
                if (!trigger) {
                  showToastMessage('Please select a trigger', 'error');
                  return;
                }

                const stepValues = localSteps.filter(s => s.msg && s.msg.trim());
                if (stepValues.length === 0) {
                  showToastMessage('Please add at least one step with a message', 'error');
                  return;
                }

                const steps = stepValues.map((s, index) => ({
                  step_number: index + 1,
                  day_label: s.day || `Day ${index * 7}`,
                  day_offset: index * 7,
                  message: s.msg.trim(),
                  is_active: true
                }));

                try {
                  const result = await dispatch(createDripCampaign({
                    name,
                    trigger,
                    steps,
                    is_active: true,
                    color: '#4f46e5'
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    await dispatch(getDripCampaigns({
                      per_page: itemsPerPage,
                      page: currentPage,
                      search: searchTerm,
                      status: statusFilter,
                      trigger: triggerFilter
                    }));
                    await dispatch(getDripCampaignStats());
                    showToastMessage(`🔄 "${name}" created successfully!`, 'success');
                  }
                } catch (err) {
                  console.error('Create campaign error:', err);
                  showToastMessage(err.message || 'Failed to create campaign', 'error');
                }
              }}
            >
              {isSubmittingRedux ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </div>
      );
    };

    openModal(<CreateModalContent />);
  };

  // ==================== EDIT CAMPAIGN ====================

  const handleEditCampaign = (campaign) => {
    setEditingCampaignId(campaign.id);

    console.log('📝 Editing campaign:', campaign);
    console.log('📝 Campaign steps:', campaign.steps);

    // Extract steps from campaign data - handle multiple possible formats
    let stepsArray = [];
    
    // Check if steps is an array and has items
    if (campaign.steps && Array.isArray(campaign.steps)) {
      stepsArray = campaign.steps;
      console.log('📋 Steps from campaign.steps:', stepsArray.length);
    } 
    // Check if steps_data exists
    else if (campaign.steps_data && Array.isArray(campaign.steps_data)) {
      stepsArray = campaign.steps_data;
      console.log('📋 Steps from campaign.steps_data:', stepsArray.length);
    }
    
    // If steps is empty or not found, try to fetch from API
    if (stepsArray.length === 0) {
      console.log('⚠️ No steps found in campaign data, fetching from API...');
      
      // Show loading state
      openModal(
        <div>
          <div className="modal-header">
            <div className="modal-title"><Loader2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Loading Campaign...</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Loader2 size={20} /></div>
              <div>Loading campaign steps...</div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          </div>
        </div>
      );

      // Fetch campaign details from API
      const fetchCampaignDetails = async () => {
        try {
          const result = await dispatch(getDripCampaign(campaign.id)).unwrap();
          console.log('✅ Fetched campaign details:', result);
          
          // Try to extract steps from the API response
          let fetchedSteps = [];
          if (result.campaign?.steps && Array.isArray(result.campaign.steps)) {
            fetchedSteps = result.campaign.steps;
          } else if (result.steps && Array.isArray(result.steps)) {
            fetchedSteps = result.steps;
          } else if (result.data?.steps && Array.isArray(result.data.steps)) {
            fetchedSteps = result.data.steps;
          }
          
          console.log('📋 Fetched steps:', fetchedSteps.length);
          
          // Close loading modal
          closeModal();
          
          // Open edit modal with fetched steps or empty state
          openEditModalWithSteps(campaign, fetchedSteps);
          
        } catch (err) {
          console.error('❌ Error fetching campaign steps:', err);
          closeModal();
          
          // Open edit modal with empty steps
          showToastMessage('Could not load campaign steps. You can add new steps.', 'warning');
          openEditModalWithSteps(campaign, []);
        }
      };
      
      fetchCampaignDetails();
      return;
    }

    // If we have steps, open the edit modal directly
    openEditModalWithSteps(campaign, stepsArray);
  };

  // Helper function to open the edit modal with steps
  const openEditModalWithSteps = (campaign, stepsArray) => {
    // Map steps to the format expected by the edit modal
    const initialEditSteps = stepsArray.length > 0
      ? stepsArray.map((s, index) => ({
          id: s.id || Date.now() + Math.random() + index,
          day: s?.day_label || s?.day || `Day ${s?.day_offset || index * 7}`,
          msg: s?.message || s?.msg || '',
          step_number: s?.step_number || index + 1,
          day_offset: s?.day_offset || index * 7
        }))
      : [
          { 
            id: Date.now() + 1, 
            day: 'Day 0', 
            msg: '', 
            step_number: 1,
            day_offset: 0
          }
        ];

    console.log('📝 Initial edit steps:', initialEditSteps);

    // Set the edit steps state
    setEditSteps(initialEditSteps);

    // Set name and trigger values in refs
    setTimeout(() => {
      if (editNameRef.current) {
        editNameRef.current.value = campaign.name || '';
      }
      if (editTriggerRef.current) {
        editTriggerRef.current.value = campaign.trigger || '';
      }
    }, 50);

    // Open the edit modal
    const EditModalContent = () => {
      // Use local state for steps inside the modal
      const [localSteps, setLocalSteps] = useState(initialEditSteps);

      // Sync with editSteps state when it changes
      useEffect(() => {
        console.log('🔄 editSteps changed:', editSteps);
        if (editSteps.length > 0) {
          setLocalSteps(editSteps);
        }
      }, [editSteps]);

      const handleAddStep = () => {
        const newId = Date.now() + Math.random();
        const nextDay = localSteps.length * 7;
        const newSteps = [...localSteps, { 
          id: newId, 
          day: `Day ${nextDay}`, 
          msg: '',
          step_number: localSteps.length + 1,
          day_offset: nextDay
        }];
        setLocalSteps(newSteps);
        setEditSteps(newSteps);
      };

      const handleRemoveStep = (id) => {
        if (localSteps.length <= 1) {
          showToastMessage('Cannot remove the last step', 'error');
          return;
        }
        const newSteps = localSteps.filter(s => s.id !== id);
        setLocalSteps(newSteps);
        setEditSteps(newSteps);
      };

      const handleStepChange = (id, field, value) => {
        const newSteps = localSteps.map(s =>
          s.id === id ? { ...s, [field]: value } : s
        );
        setLocalSteps(newSteps);
        setEditSteps(newSteps);
      };

      // Check if we have any steps with messages
      const hasStepsWithMessages = localSteps.some(s => s.msg && s.msg.trim());

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Campaign: {campaign.name}</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                Trigger: <strong>{campaign.trigger}</strong> · 
                <strong>{campaign.enrolled || 0}</strong> enrolled · 
                <strong>{localSteps.length}</strong> steps
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Campaign Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                ref={editNameRef} 
                defaultValue={campaign.name || ''}
                placeholder="Enter campaign name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Trigger <span style={{ color: 'var(--red)' }}>*</span></label>
              <select 
                className="form-select" 
                ref={editTriggerRef} 
                defaultValue={campaign.trigger || ''}
              >
                <option value="">Select trigger...</option>
                <option value="Member status = Visitor">Member status = Visitor (new visitor)</option>
                <option value="Member status changes to Active">Member status changes to Active</option>
                <option value="Member opts in with SMS keyword">Member opts in with SMS keyword</option>
                <option value="Member absent for 30+ days">Member absent for 30+ days</option>
                <option value="Manual enrollment by admin">Manual enrollment by admin</option>
              </select>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                  Campaign Steps
                  {localSteps.length === 0 && (
                    <span style={{ fontSize: '11px', fontWeight: '400', color: 'var(--text-light)', marginLeft: '8px' }}>
                      (No steps found. Add your first step below.)
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>{localSteps.length} steps</span>
              </div>
              
              {localSteps.length === 0 ? (
                // Show empty state
                <div style={{ 
                  textAlign: 'center', 
                  padding: '30px 20px', 
                  background: 'var(--cream)',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><FileEdit size={40} strokeWidth={1.5} /></div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--navy)' }}>No Steps Added Yet</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                    Click the "Add Step" button below to create your first campaign step.
                  </div>
                </div>
              ) : (
                // Show steps
                localSteps.map((s, index) => {
                  const dayValue = s?.day || `Day ${s?.day_offset || index * 7}`;
                  const msgValue = s?.msg || '';
                  return (
                    <div key={s.id || index} style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        minWidth: '52px',
                        padding: '4px 8px',
                        background: 'var(--navy)',
                        color: 'var(--gold-light)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '700',
                        textAlign: 'center',
                        flexShrink: 0,
                        marginTop: '4px'
                      }}>
                        <input
                          type="text"
                          value={dayValue}
                          onChange={(e) => handleStepChange(s.id || index, 'day', e.target.value)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--gold-light)',
                            fontSize: '11px',
                            fontWeight: '700',
                            textAlign: 'center',
                            width: '100%',
                            outline: 'none'
                          }}
                          placeholder="Day X"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <textarea
                          className="form-textarea"
                          rows="2"
                          style={{ minHeight: '50px' }}
                          value={msgValue}
                          onChange={(e) => handleStepChange(s.id || index, 'msg', e.target.value)}
                          placeholder={`Enter message for ${dayValue}...`}
                        />
                      </div>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => handleRemoveStep(s.id || index)}
                        style={{ color: 'var(--red)', marginTop: '4px' }}
                        title="Remove step"
                        disabled={localSteps.length <= 1}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })
              )}
              
              <button
                className="btn btn-gold btn-sm"
                onClick={handleAddStep}
                style={{ marginTop: '4px' }}
              >
                + Add Step
              </button>
            </div>
            
            {!hasStepsWithMessages && localSteps.length > 0 && (
              <div style={{
                marginTop: '12px',
                padding: '10px 12px',
                background: 'var(--amber-bg)',
                borderRadius: '8px',
                fontSize: '12px',
                color: 'var(--amber)'
              }}>
                <AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Please add messages to your steps before saving.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              disabled={isSubmittingRedux}
              onClick={async () => {
                const name = editNameRef.current?.value?.trim() || '';
                const trigger = editTriggerRef.current?.value || '';

                if (!name) {
                  showToastMessage('Please enter a campaign name', 'error');
                  return;
                }
                if (!trigger) {
                  showToastMessage('Please select a trigger', 'error');
                  return;
                }

                const stepValues = localSteps.filter(s => s.msg && s.msg.trim());
                if (stepValues.length === 0) {
                  showToastMessage('Please add at least one step with a message', 'error');
                  return;
                }

                const steps = stepValues.map((s, index) => ({
                  step_number: index + 1,
                  day_label: s.day || `Day ${index * 7}`,
                  day_offset: index * 7,
                  message: s.msg.trim(),
                  is_active: true
                }));

                try {
                  const result = await dispatch(updateDripCampaign({
                    id: campaign.id,
                    data: {
                      name,
                      trigger,
                      steps
                    }
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    await dispatch(getDripCampaigns({
                      per_page: itemsPerPage,
                      page: currentPage,
                      search: searchTerm,
                      status: statusFilter,
                      trigger: triggerFilter
                    }));
                    await dispatch(getDripCampaignStats());
                    showToastMessage(`✅ "${name}" updated successfully!`, 'success');
                  }
                } catch (err) {
                  console.error('Update campaign error:', err);
                  showToastMessage(err.message || 'Failed to update campaign', 'error');
                }
              }}
            >
              {isSubmittingRedux
                ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Saving...</>
                : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}
            </button>
          </div>
        </div>
      );
    };

    openModal(<EditModalContent />);
  };

  // ==================== DELETE CAMPAIGN ====================

  const handleDeleteCampaign = (campaign) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Campaign</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{campaign.name}</strong>?
          </p>
          <div style={{
            padding: '12px',
            background: 'var(--red-bg)',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--red)'
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will permanently remove the campaign and all enrolled members.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-danger btn-sm"
            disabled={isSubmittingRedux}
            onClick={async () => {
              try {
                const result = await dispatch(deleteDripCampaign(campaign.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getDripCampaigns({
                    per_page: itemsPerPage,
                    page: currentPage,
                    search: searchTerm,
                    status: statusFilter,
                    trigger: triggerFilter
                  }));
                  await dispatch(getDripCampaignStats());
                  showToastMessage(`🗑️ "${campaign.name}" deleted`, 'error');
                }
              } catch (err) {
                console.error('Delete campaign error:', err);
                showToastMessage(err.message || 'Failed to delete campaign', 'error');
              }
            }}
          >
            Delete Campaign
          </button>
        </div>
      </div>
    );
  };

  // ==================== DUPLICATE CAMPAIGN ====================

  const handleDuplicateCampaign = async (campaign) => {
    try {
      const result = await dispatch(duplicateDripCampaign(campaign.id)).unwrap();
      if (result.success) {
        await dispatch(getDripCampaigns({
          per_page: itemsPerPage,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          trigger: triggerFilter
        }));
        await dispatch(getDripCampaignStats());
        showToastMessage(`📋 "${result.campaign.name}" created`, 'success');
      }
    } catch (err) {
      console.error('Duplicate campaign error:', err);
      showToastMessage(err.message || 'Failed to duplicate campaign', 'error');
    }
  };

  // ==================== VIEW ANALYTICS ====================

  const handleViewAnalytics = async (campaign) => {
    try {
      await dispatch(getDripCampaignAnalytics(campaign.id));

      const rate = analytics?.completion_rate || 0;
      const totalEnrollments = analytics?.total_enrollments || 0;
      const completedEnrollments = analytics?.completed_enrollments || 0;

      openModal(
        <div>
          <div className="modal-header">
            <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Campaign Analytics: {campaign.name}</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{totalEnrollments}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Enrolled</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--green)' }}>{completedEnrollments}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Completed</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--blue)' }}>{rate}%</div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Rate</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--purple)' }}>{campaign.steps || 0}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Steps</div>
              </div>
            </div>
            <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>Progress Breakdown</div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span>Completion</span>
                  <span>{rate}%</span>
                </div>
                <div className="progress-bar" style={{ height: '8px' }}>
                  <div className="progress-fill" style={{ width: `${rate}%`, background: rate >= 70 ? 'var(--green)' : rate >= 50 ? 'var(--amber)' : 'var(--red)' }}></div>
                </div>
              </div>
              {analytics?.step_breakdown && analytics.step_breakdown.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>Step Breakdown</div>
                  {analytics.step_breakdown.map((step, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '2px 0' }}>
                      <span>{step.day_label || `Step ${step.step}`}</span>
                      <span>{step.percentage}% reached ({step.reached || 0})</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: '12px', color: 'var(--text-mid)', marginTop: '8px' }}>
                <div><TrendingUp size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{campaign.is_active ? 'Active' : 'Paused'}</div>
                <div><Target size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{campaign.trigger}</div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
            <button className="btn btn-gold btn-sm" onClick={() => {
              showToastMessage('📥 Report exported', 'info');
              closeModal();
            }}><Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export Report</button>
          </div>
        </div>
      );
    } catch (err) {
      console.error('Analytics error:', err);
      showToastMessage(err.message || 'Failed to load analytics', 'error');
    }
  };

  // ==================== SEND BROADCAST ====================

  const handleOpenBroadcastModal = (campaign) => {
    // Use local state inside the modal component
    const BroadcastModalContent = () => {
      const [message, setMessage] = useState('');

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Send Broadcast to {campaign.name}</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{
              background: 'var(--cream)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
                Send a broadcast message to all <strong>{campaign.enrolled || 0}</strong> enrolled members.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
              <textarea
                className="form-textarea"
                rows="4"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your broadcast message..."
                autoFocus
              />
              <div className="form-hint">
                Use {'{FirstName}'} for personalisation
              </div>
            </div>
            <div style={{
              padding: '10px 12px',
              background: 'var(--cream)',
              borderRadius: '8px',
              fontSize: '12px',
              color: 'var(--text-light)',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{campaign.enrolled || 0} recipients</span>
              <span><MessageSquare size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{message.length} characters</span>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              disabled={isSubmittingRedux || !message.trim()}
              onClick={async () => {
                if (!message.trim()) {
                  showToastMessage('Please enter a message', 'error');
                  return;
                }

                try {
                  const result = await dispatch(sendBroadcastToCampaign({
                    id: campaign.id,
                    message: message.trim(),
                    // sender_id: 'CHURCH'
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    showToastMessage(`📨 Broadcast sent to ${campaign.enrolled || 0} members!`, 'success');
                    await dispatch(getDripCampaign(campaign.id));
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to send broadcast', 'error');
                }
              }}
            >
              {isSubmittingRedux
                ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Sending...</>
                : <><Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send Broadcast</>}
            </button>
          </div>
        </div>
      );
    };

    openModal(<BroadcastModalContent />);
  };

  // ==================== TRIGGER CAMPAIGN FLOW ====================

  const handleTriggerFlow = async (campaign) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Zap size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Trigger Campaign Flow</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to trigger the flow for <strong>{campaign.name}</strong>?
          </p>
          <div style={{
            padding: '12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              This will send the next message to all <strong>{campaign.enrolled || 0}</strong> enrolled members.
            </div>
          </div>
          <div style={{
            padding: '12px',
            background: 'var(--amber-bg)',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--amber)'
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This action cannot be undone. Messages will be sent immediately.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmittingRedux}
            onClick={async () => {
              try {
                const result = await dispatch(triggerCampaignFlow(campaign.id)).unwrap();

                if (result.success) {
                  closeModal();
                  showToastMessage(`⏳ Campaign flow triggered for ${campaign.enrolled || 0} members!`, 'success');
                  await dispatch(getDripCampaign(campaign.id));
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to trigger campaign flow', 'error');
              }
            }}
          >
            {isSubmittingRedux
              ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Triggering...</>
              : <><Zap size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Trigger Flow</>}
          </button>
        </div>
      </div>
    );
  };

  // ==================== MANAGE MEMBERS ====================

  const handleManageMembers = (campaign) => {
    // Helper function to get full name
    const getFullName = (member) => {
      if (!member) return 'Unknown Member';
      const firstName = member.first_name || '';
      const lastName = member.last_name || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown Member';
    };

    // Helper function to get initials
    const getInitials = (member) => {
      if (!member) return 'U';
      const firstName = member.first_name || '';
      const lastName = member.last_name || '';
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
    };

    let searchTimeout = null;

    const searchMembers = async (query, setState) => {
      if (!query || query.length < 2) {
        setState({ availableMembers: [], isSearching: false });
        return;
      }

      setState({ isSearching: true });

      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setState({ availableMembers: [], isSearching: false });
          return;
        }

        const response = await apiClient.get('/members', {
          params: { search: query, per_page: 10 }
        });

        const membersData = response.data?.data?.data || [];
        const currentEnrollments = Array.isArray(enrollments) ? enrollments : [];
        const enrolledIds = currentEnrollments.map(e => e.member_id);
        const available = membersData.filter(m => !enrolledIds.includes(m.id));

        setState({ availableMembers: available, isSearching: false });
      } catch (error) {
        console.error('Error searching members:', error);
        setState({ availableMembers: [], isSearching: false });
      }
    };

    const handleEnrollMember = async (memberId, memberName) => {
      try {
        const result = await dispatch(enrollMemberInCampaign({
          id: campaign.id,
          member_id: memberId
        })).unwrap();

        if (result.success) {
          await dispatch(getDripCampaignEnrollments({
            id: campaign.id,
            params: { per_page: 20 }
          }));
          await dispatch(getDripCampaign(campaign.id));
          showToastMessage(`✅ ${memberName} enrolled successfully!`, 'success');
          closeModal();
          setTimeout(() => handleManageMembers(campaign), 100);
        }
      } catch (err) {
        showToastMessage(err.message || 'Failed to enroll member', 'error');
      }
    };

    const handleUnenrollMember = async (memberId, memberName) => {
      openModal(
        <div>
          <div className="modal-header">
            <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Remove Member</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
              Are you sure you want to remove <strong>{memberName}</strong> from this campaign?
            </p>
            <div style={{
              padding: '12px',
              background: 'var(--amber-bg)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--amber)'
            }}>
              <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will remove the member from the campaign.
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-danger btn-sm"
              onClick={async () => {
                try {
                  const result = await dispatch(unenrollMemberFromCampaign({
                    id: campaign.id,
                    member_id: memberId
                  })).unwrap();
                  if (result.success) {
                    closeModal();
                    await dispatch(getDripCampaignEnrollments({
                      id: campaign.id,
                      params: { per_page: 20 }
                    }));
                    await dispatch(getDripCampaign(campaign.id));
                    showToastMessage(`✅ ${memberName} removed from campaign`, 'success');
                    setTimeout(() => handleManageMembers(campaign), 100);
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to remove member', 'error');
                }
              }}
            >
              Remove Member
            </button>
          </div>
        </div>
      );
    };

    // Create the main modal content with local state
    const MembersModalContent = () => {
      const [searchTerm, setSearchTerm] = useState('');
      const [results, setResults] = useState([]);
      const [searching, setSearching] = useState(false);
      const [loadingEnrollments, setLoadingEnrollments] = useState(true);
      const [localEnrollments, setLocalEnrollments] = useState([]);

      useEffect(() => {
        const loadEnrollments = async () => {
          try {
            setLoadingEnrollments(true);
            const result = await dispatch(getDripCampaignEnrollments({
              id: campaign.id,
              params: { per_page: 20 }
            })).unwrap();
            console.log('✅ Enrollments fetch result:', result);
            if (result.enrollments) {
              setLocalEnrollments(result.enrollments);
            }
          } catch (err) {
            console.error('❌ Error loading enrollments:', err);
          } finally {
            setLoadingEnrollments(false);
          }
        };

        loadEnrollments();
      }, [campaign.id, dispatch]);

      useEffect(() => {
        if (Array.isArray(enrollments) && enrollments.length > 0) {
          setLocalEnrollments(enrollments);
        }
      }, [enrollments]);

      const handleSearch = (query) => {
        setSearchTerm(query);
        if (searchTimeout) clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          searchMembers(query, (state) => {
            setResults(state.availableMembers || []);
            setSearching(state.isSearching || false);
          });
        }, 300);
      };

      if (loadingEnrollments) {
        return (
          <div>
            <div className="modal-header">
              <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Enrolled Members: {campaign.name}</div>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Loader2 size={20} /></div>
                <div>Loading enrolled members...</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
            </div>
          </div>
        );
      }

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Enrolled Members: {campaign.name}</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{
              background: 'var(--cream)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ flex: 1 }}
                  placeholder="Search members by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>

              {searching && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-light)' }}>
                  Searching...
                </div>
              )}

              {results.length > 0 && (
                <div style={{
                  marginTop: '8px',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  padding: '4px'
                }}>
                  {results.map(m => {
                    const fullName = getFullName(m);
                    const email = m.email || '';
                    const phone = m.phone || '';
                    const displayInfo = email || phone;

                    return (
                      <div key={m.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{fullName}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{displayInfo || 'No contact info'}</div>
                        </div>
                        <button
                          className="btn btn-gold btn-sm"
                          onClick={() => handleEnrollMember(m.id, fullName)}
                        >
                          + Add
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchTerm.length >= 2 && !searching && results.length === 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-mid)' }}>
                  No members found. Try a different search term.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                Total Enrolled: <strong>{localEnrollments.length || 0}</strong>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  className="btn btn-gold btn-sm"
                  onClick={() => {
                    closeModal();
                    handleOpenBroadcastModal(campaign);
                  }}
                  title="Send broadcast to all enrolled members"
                >
                  <Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send Broadcast
                </button>
                <button
                  className="btn btn-gold btn-sm"
                  onClick={() => {
                    closeModal();
                    handleTriggerFlow(campaign);
                  }}
                  title="Trigger campaign flow"
                >
                  <Zap size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Trigger Flow
                </button>
                <button
                  className="btn btn-gold btn-sm"
                  onClick={() => {
                    closeModal();
                    openExportMembersModal(campaign, localEnrollments);
                  }}
                  title="Export member list"
                >
                  <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export
                </button>
              </div>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {localEnrollments.length > 0 ? (
                localEnrollments.map((enrollment, index) => {
                  const member = enrollment.member || { first_name: 'Unknown', last_name: 'Member' };
                  const fullName = member.full_name || getFullName(member);
                  const progress = enrollment.progress_percentage || 0;
                  const status = enrollment.status || (enrollment.completed_at ? 'Completed' : 'In Progress');
                  const colors = ['#4f46e5', '#059669', '#d97706', '#7c3aed', '#0891b2', '#dc2626'];
                  const color = colors[index % colors.length];
                  const initials = member.initials || getInitials(member);

                  return (
                    <div key={enrollment.id || index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 0',
                      borderBottom: '1px solid var(--border-light)'
                    }}>
                      <div className="member-row-avatar" style={{
                        background: color,
                        width: '28px',
                        height: '28px',
                        fontSize: '11px'
                      }}>
                        {initials}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '500' }}>{fullName}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>
                          {member.email || member.phone || ''} · Progress: {progress}% · {status}
                        </div>
                      </div>
                      <div className="progress-bar" style={{ width: '50px' }}>
                        <div className="progress-fill" style={{ width: `${progress}%`, background: 'var(--blue)' }}></div>
                      </div>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => handleUnenrollMember(enrollment.member_id, fullName)}
                        title={`Remove ${fullName} from campaign`}
                        style={{ color: 'var(--red)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
                  No members enrolled in this campaign yet.
                </div>
              )}
              {localEnrollments.length > 20 && (
                <div style={{ textAlign: 'center', padding: '8px', fontSize: '11px', color: 'var(--text-light)' }}>
                  Showing 20 of {localEnrollments.length} enrolled members
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          </div>
        </div>
      );
    };

    openModal(<MembersModalContent />);
  };

  // ==================== EXPORT MEMBERS (FIXED – uses real data) ====================

  const openExportMembersModal = (campaign, membersData = []) => {
    if (!membersData || membersData.length === 0) {
      showToastMessage('No members to export', 'error');
      return;
    }

    // Map enrollments to export fields
    const members = membersData.map(enrollment => {
      const member = enrollment.member || {};
      const fullName = member.full_name || 
        `${member.first_name || ''} ${member.last_name || ''}`.trim() || 
        'Unknown';
      const progress = enrollment.progress_percentage || 0;
      let status = enrollment.status || 'In Progress';
      if (enrollment.completed_at) {
        status = 'Completed';
      }
      return {
        name: fullName,
        progress: `${progress}%`,
        status: status
      };
    });

    const fields = [
      { key: 'name', label: 'Member Name' },
      { key: 'progress', label: 'Progress' },
      { key: 'status', label: 'Status' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export Members</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export members for <strong>{campaign.name}</strong>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const formattedData = formatForExport(members, fields);
                    const filename = getExportFilename(`members_${campaign.name.toLowerCase().replace(/\s/g, '_')}`);

                    switch (format) {
                      case 'CSV':
                        exportToCSV(formattedData, filename);
                        break;
                      case 'JSON':
                        exportToJSON(formattedData, filename);
                        break;
                      case 'Excel':
                        exportToExcel(formattedData, filename);
                        break;
                      case 'PDF':
                        exportToPDF(formattedData, `Members - ${campaign.name}`, filename);
                        break;
                      default:
                        showToastMessage('Export format not supported', 'error');
                        return;
                    }
                    showToastMessage(`📥 ${members.length} members exported for "${campaign.name}" as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{
                    padding: '8px',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{members.length} members will be exported</span>
            <span style={{ fontSize: '11px' }}>{campaign.name}</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // ==================== VIEW STEPS ====================

  const handleViewSteps = (campaign) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><ClipboardList size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Campaign Steps: {campaign.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Loader2 size={20} /></div>
            <div>Loading steps...</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );

    const fetchCampaignSteps = async () => {
      try {
        const result = await dispatch(getDripCampaign(campaign.id)).unwrap();
        console.log('📋 Campaign steps data:', result);

        let steps = [];
        if (result.campaign?.steps && Array.isArray(result.campaign.steps)) {
          steps = result.campaign.steps;
        } else if (campaign.steps && Array.isArray(campaign.steps)) {
          steps = campaign.steps;
        }

        console.log('📋 Steps found:', steps.length);

        closeModal();

        openModal(
          <div>
            <div className="modal-header">
              <div className="modal-title"><ClipboardList size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Campaign Steps: {campaign.name}</div>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {steps.length > 0 ? (
                steps.map((step, i) => {
                  const dayLabel = step?.day_label || `Day ${step?.day_offset || i * 7}`;
                  const message = step?.message || `Step ${i + 1} message for ${campaign.name}…`;
                  return (
                    <div key={step?.id || i} style={{ display: 'flex', gap: '12px', marginBottom: '12px', padding: '10px', background: 'var(--cream)', borderRadius: '8px' }}>
                      <div style={{ minWidth: '52px', padding: '4px 8px', background: 'var(--navy)', color: 'var(--gold-light)', borderRadius: '6px', fontSize: '11px', fontWeight: '700', textAlign: 'center', flexShrink: 0 }}>
                        {dayLabel}
                      </div>
                      <div style={{ flex: 1, fontSize: '12px', color: 'var(--text)' }}>
                        {message}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
                  <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><FileEdit size={40} strokeWidth={1.5} /></div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--navy)' }}>No steps defined</div>
                  <div style={{ fontSize: '13px', marginTop: '4px' }}>
                    This campaign has {campaign.steps || 0} steps but they haven't been loaded.
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
            </div>
          </div>
        );
      } catch (err) {
        console.error('❌ Error fetching campaign steps:', err);
        closeModal();
        showToastMessage('Failed to load campaign steps', 'error');
      }
    };

    fetchCampaignSteps();
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    total: 0,
    active: 0,
    inactive: 0,
    total_enrolled: 0,
    total_completed: 0,
    avg_completion_rate: 0
  };

  const uniqueTriggers = getUniqueTriggers();

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <RefreshCw size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Build multi-step SMS journeys that nurture members automatically over days or weeks.
        </div>
        <button className="btn btn-gold btn-sm" onClick={openCreateDrip} disabled={loading} title="Create new drip campaign">
          + New Campaign
        </button>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Active Campaigns</div>
          <div className="stat-value">{statsData.active || 0}</div>
          <div className="stat-change up">running now</div>
          <div className="stat-icon"><Zap size={20} /></div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Members Enrolled</div>
          <div className="stat-value">{statsData.total_enrolled || 0}</div>
          <div className="stat-change up">across all campaigns</div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Completion Rate</div>
          <div className="stat-value">{Math.round(statsData.avg_completion_rate || 0)}%</div>
          <div className="stat-change up">↑ strong</div>
          <div className="stat-icon"><Target size={20} /></div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Messages Sent</div>
          <div className="stat-value">{statsData.total_completed || 0}</div>
          <div className="stat-change up">↑ all time</div>
          <div className="stat-icon"><Send size={20} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={16} /></span>
            <input
              type="text"
              placeholder="Search campaigns…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search by name or trigger"
            />
          </div>
          <select
            className="form-select"
            style={{ width: '180px' }}
            value={triggerFilter}
            onChange={(e) => {
              setTriggerFilter(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by trigger"
            disabled={loading}
          >
            <option value="">All Triggers</option>
            {uniqueTriggers.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            className="form-select"
            style={{ width: '140px' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by status"
            disabled={loading}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            {campaigns.length} campaigns found
          </div>
        </div>
      </div>

      {/* Campaigns Content - Loading appears here when campaigns are loading */}
      {loading && campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading campaigns...</div>
        </div>
      ) : campaigns.length > 0 ? (
        campaigns.map(c => {
          const rate = c.enrolled > 0 ? Math.round((c.completed || 0) / c.enrolled * 100) : 0;
          return (
            <div key={c.id} className="card" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `${c.color || '#4f46e5'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><RefreshCw size={24} /></div>
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{c.name}</div>
                    <span className={`badge ${c.is_active ? 'badge-green' : 'badge-red'}`}>{c.is_active ? 'Active' : 'Paused'}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Trigger: {c.trigger} · {Array.isArray(c.steps) ? c.steps.length : c.steps || 0} steps</div>
                </div>
                <div style={{ display: 'flex', gap: '20px', textAlign: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{c.enrolled || 0}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>enrolled</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--green)' }}>{c.completed || 0}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>completed</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: rate >= 70 ? 'var(--green)' : rate >= 50 ? 'var(--amber)' : 'var(--red)' }}>{rate}%</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>rate</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginLeft: '16px', flexWrap: 'wrap' }}>
                  <label className="toggle" title={c.is_active ? 'Click to pause' : 'Click to activate'}>
                    <input type="checkbox" checked={c.is_active !== false} onChange={(e) => toggleCampaign(c.id, e.target.checked)} disabled={loading} />
                    <span className="toggle-slider"></span>
                  </label>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleEditCampaign(c)}
                    title={`Edit ${c.name}`}
                    disabled={loading}
                  >
                    <Pencil size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Edit
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleDeleteCampaign(c)}
                    title={`Delete ${c.name}`}
                    style={{ color: 'var(--red)' }}
                    disabled={loading}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '6px' }}>
                    Campaign Progress ({c.completed || 0}/{c.enrolled || 0} completed)
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleViewAnalytics(c)}
                      title="View campaign analytics"
                      disabled={loading}
                    >
                      <BarChart3 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Analytics
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleManageMembers(c)}
                      title="Manage enrolled members"
                      disabled={loading}
                    >
                      <Users size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Members
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleViewSteps(c)}
                      title="View campaign steps"
                      disabled={loading}
                    >
                      <ClipboardList size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Steps
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDuplicateCampaign(c)}
                      title="Duplicate campaign"
                      disabled={loading || isSubmittingRedux}
                    >
                      <Copy size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Copy
                    </button>
                  </div>
                </div>
                <div className="progress-bar" style={{ marginTop: '4px' }}>
                  <div className="progress-fill" style={{ width: `${rate}%`, background: c.color || '#4f46e5' }}></div>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="empty-state">
          <div className="empty-icon"><RefreshCw size={32} strokeWidth={1.5} /></div>
          <div className="empty-title">No campaigns found</div>
          <div className="empty-text">Try adjusting your search or filters</div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '20px',
          padding: '16px',
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            Showing <strong>{((currentPageNum - 1) * perPage) + 1}</strong> to <strong>{Math.min(currentPageNum * perPage, totalItems)}</strong> of <strong>{totalItems}</strong> campaigns
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
              <select
                className="form-select"
                style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                title="Items per page"
                disabled={loading}
              >
                <option value={2}>2</option>
                <option value={4}>4</option>
                <option value={6}>6</option>
                <option value={10}>10</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={goToPreviousPage}
                disabled={currentPageNum === 1 || loading}
                style={{
                  opacity: currentPageNum === 1 ? 0.5 : 1,
                  cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer'
                }}
                title="Previous page"
              >
                ← Prev
              </button>

              {getPageNumbers().map(page => (
                <button
                  key={page}
                  className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                  title={`Go to page ${page}`}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-ghost btn-sm"
                onClick={goToNextPage}
                disabled={currentPageNum === totalPages || loading}
                style={{
                  opacity: currentPageNum === totalPages ? 0.5 : 1,
                  cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer'
                }}
                title="Next page"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .progress-bar {
          width: 100%;
          height: 6px;
          background: var(--cream);
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.5s ease;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
        .btn-icon {
          padding: 4px 6px;
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
      `}</style>
    </>
  );
};

export default DripCampaigns;